// React
import React, { useState, useEffect, useRef, MouseEvent, CSSProperties } from 'react';

// types
import { BiReportItem } from '../types';

import { Insights } from '../../../../global';

// app
import sessionRepo from '../../../core/repo/sessionRepo';
import { utilSvc, validSvc } from '../../../core/services/exports';
import { ngDelegate, ngContext } from '../../../core/services/ng.delegate';

// Components
// tbd PopUp control, too much going on with height/width currently to mess with it
import PageTopComp from '../../../core/components/PageTop';
import BiVisual, { BiVisualMethods } from './bi.visual';

// data and store
import biRepo from './biRepo';
import useMenuStore from './menuStore';
import LazyScriptLoader from '../../../core/components/LazyScriptLoader';

// Render State
// state is only a trigger to move the display to the next render state, no "code behind" data is stored directly on the state (see store below)
enum BiRender {
    Init,
    NoReports,
    Ready,
    Popup,
}

// Store Properties
// note React render state and React store state are completely different, anything the code is storing / using "behind the scenes" is put in the store
// https://stackoverflow.com/questions/41584647/when-do-i-choose-react-state-vs-redux-store
type BiStore = {
    isDev: boolean;
    loadVis: boolean;
    report: BiReportItem | null;
    visUrl: string;
    insights: Insights;
    biVersion: string;
};

// CONTROLLER
const ReportBiCtl = (): JSX.Element => {
    const menuStore = useMenuStore();

    // code store (store is persisted across renders, this is the location for code state and data)
    // note "store" is the React concept of Redux
    const _biStore = useRef<BiStore>({
        isDev: false,
        loadVis: false,
        report: null,
        visUrl: '',
        insights: {} as Insights,
        biVersion: '',
    });

    // render state (what the user sees, unlike Angular the render and store are seperate)
    const [render, setRender] = useState<BiRender>(BiRender.Init);

    // INIT
    useEffect(() => {
        // unlike Angular, React init hook runs AFTER the first render
        // use state to trigger a re-render once init is complete

        // setup store context
        const store = _biStore.current;
        const ngCtx = ngContext();
        store.isDev = ngCtx.isDev;
        store.insights = ngCtx.insights;
        store.biVersion = ngCtx.biVersion;

        // get last report from viewstate
        const lastReportClaim = initViewState();

        // setup menu store
        const usr = ngCtx.User;
        const rxBizModels = !!usr ? utilSvc.aff.rxModelList(utilSvc.aff.findStatus(usr.AffList), false) : [];
        const menuInfo = menuStore.setMenu(ngCtx.User, rxBizModels, lastReportClaim);
        if (menuInfo.itemCount < 1) {
            setRender(BiRender.NoReports);
            return;
        }

        // return visit shows last report
        if (!!menuInfo.activeReport) {
            store.report = menuInfo.activeReport;
            openPopup(store.report);
            return;
        }

        // ready
        setRender(BiRender.Ready);

        // destroy
        return destroy;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function destroy(): void {
        menuStore.unload();
    }

    function initViewState(): string | null {
        // get last report opened in session
        const vs = sessionRepo.get('vs-bi-facility');
        if (!!!vs || !validSvc.isText(vs.claim)) {
            return null;
        }
        return vs.claim;
    }

    // REPORT POPUP
    function openPopup(report: BiReportItem): void {
        // is store already loading visual?
        if (_biStore.current.loadVis) {
            return;
        }

        // bi element not ready
        // v3 embedding API is a module that registers the <tableau-viz> custom element, no global window.tableau anymore
        if (!!!_biStore.current.isDev && !!!customElements.get('tableau-viz')) {
            ngDelegate.ok('BI Loading', 'BI tools are still downloading, please choose your report again shortly.');
            return;
        }

        _biStore.current.loadVis = true;
        _biStore.current.report = report;
        _biStore.current.visUrl = '';
        menuStore.setActiveReport(report);

        // ASYNC: load bi report url
        biChangeVisual(report.claim);
    }

    function closePopup(): void {
        _biStore.current.loadVis = false;
        menuStore.setActiveReport(null);
        setRender(BiRender.Ready);
    }

    function biChangeVisual(claim: string): void {
        // get chart url from api
        ngDelegate.wait(true);

        // dev mode: mock display (don't call server)
        if (_biStore.current.isDev) {
            _biStore.current.visUrl = 'local';
            setRender(BiRender.Popup);
        } else {
            getBiPurl(claim); // ASYNC
        }
    }

    const getBiPurl = async (claim: string): Promise<void> => {
        try {
            const r = await biRepo.getBiViewPurl(claim);
            _biStore.current.visUrl = r;
            setRender(BiRender.Popup);
        } catch (err) {
            ngDelegate.wait(false);
            _biStore.current.loadVis = false;
            closePopup();
            repoError(err, 'analytics.getBiViewPurl');
        }
    };

    function onBiLoaded(): void {
        // event returned from Tableau when visual is loaded
        ngDelegate.wait(false);

        const store = _biStore.current;
        if (!store.loadVis || !!!store.report) {
            return;
        }
        _biStore.current.loadVis = false;
        sessionRepo.set('vs-bi-facility', { claim: store.report.claim });
    }

    // function onBiFilter() {
    // 	// event filter changed, remove STOP button
    // 	try {
    // 		var stopElem:HTMLDivElement|undefined = document.querySelector("[data-tb-test-id='CancelDialog-Dialog-Floater-Root']") as HTMLDivElement;
    // 		if(!!stopElem) {
    // 			stopElem.style.display = 'none';
    // 		}
    // 		stopElem = undefined;
    // 	}
    // 	catch(err) {
    // 	}
    // }

    // ** HELPERS **

    function repoError(r: any, location: string): void {
        // handled upstream, something else is driving UI like logout, error resets, etc
        if (!!!r) {
            return;
        }

        // show message, reset UI state
        ngDelegate.warn('Unexpected api error', location, r.status || 'unknown', utilSvc.http.safeResponse(r));
    }

    // RENDER: POPUP
    const MainPopup = (): JSX.Element | null => {
        // bi visual tableau object
        const _biVisualREF = useRef<BiVisualMethods | null>(null);

        useEffect(() => {
            return destroy;
        }, []);

        function destroy(): void {
            _biVisualREF.current = null;
        }

        const store = _biStore.current;

        // report not loaded yet
        const report = store.report;
        if (!!!report) {
            return null;
        }

        // popup style
        const popContentStyle: CSSProperties = {
            top: '50px',
            left: '4%',
            width: '90%',
        };

        const popTitle = report.section + ' - ' + report.name;
        // const popState:PopupState = {
        // 	title: report.section + ' - ' + report.name,
        // 	contentStyle: popContentStyle
        // };

        function pdfExport(): void {
            _biVisualREF.current?.showPdf();
        }

        function excelExport(): void {
            _biVisualREF.current?.showExcel();
        }

        return (
            <div className="table-popup">
                <div
                    id="tablePopupContent"
                    className="table-popup-content"
                    style={popContentStyle}
                >
                    <h5
                        className="table-popup-title"
                        onClick={closePopup}
                    >
                        <span>{popTitle}</span>
                        <span className="icon-glyph icon-cancel"></span>
                    </h5>
                    <div className="bi-main-popup">
                        <p className="bi-popup-options">
                            <a onClick={pdfExport}>
                                <span className="icon-glyph icon-pdf"></span> Export to PDF
                            </a>
                            <a onClick={excelExport}>
                                <span className="icon-glyph icon-pdf"></span> Export Data
                            </a>
                        </p>
                        <BiVisual
                            ref={_biVisualREF}
                            onLoaded={onBiLoaded}
                            isDev={store.isDev}
                            url={store.visUrl}
                        />
                    </div>
                </div>
                <div
                    className="table-popup-bkg"
                    onClick={closePopup}
                >
                    &nbsp;
                </div>
            </div>
        );
    };

    type BiContentParams = {
        render: BiRender;
        openPopup: (report: BiReportItem) => void;
    };
    const BiContent = ({ render, openPopup }: BiContentParams): JSX.Element => {
        const showPopup = render == BiRender.Popup;
        const noReports = render == BiRender.NoReports;
        return (
            <>
                <Menu openPopup={openPopup} />
                <Welcome noReports={noReports} />
                {showPopup && <MainPopup />}
            </>
        );
    };

    const Welcome = ({ noReports }: { noReports: boolean }): JSX.Element => {
        const mode = _biStore.current.insights.mode;
        const msg = _biStore.current.insights[mode];

        let title = '';
        let showMenu = true;
        switch (mode) {
            case 'on':
                // maintenance is on
                title = 'Temporarily Unavailable';
                showMenu = false;
                break;
            case 'warn':
                // maintenance is scheduled
                title = 'Scheduled For Maintenance';
                break;
            default:
                title = 'Welcome to Insights';
                break;
        }

        return (
            <div className="bi-welcome">
                <h3>{title}</h3>
                {!!msg ? (
                    <>
                        <p>&nbsp;</p>
                        <p>{msg}</p>
                    </>
                ) : null}
                <p>&nbsp;</p>
                {showMenu ? (
                    <>
                        {noReports ? (
                            <p>No reports available, contact your administrator.</p>
                        ) : (
                            <p>Choose a view on the left to begin your analytics journey.</p>
                        )}
                    </>
                ) : null}
                <img
                    className="bi-welcome-img"
                    src="/assets/img/app/dash-graph.png"
                />
                <p className="bi-welcome-data-note">Insights includes this current year and 5 year historical data.</p>
            </div>
        );
    };

    const Menu = ({ openPopup }: { openPopup: (item: BiReportItem) => void }): JSX.Element | null => {
        const menuStore = useMenuStore();
        const mode = _biStore.current.insights.mode;

        useEffect(() => {}, []);

        function handleClickMenu(e: MouseEvent, rpt): void {
            e.preventDefault();
            openPopup(rpt);
        }

        // insights is off for maintenance
        if (mode == 'on') {
            return null;
        }

        // no menu
        const items = menuStore.items;
        if (items.length < 0) {
            return null;
        }
        const activeClaim = !!menuStore.activeReport ? menuStore.activeReport.claim : '';

        return (
            <section className="panel bi-left-nav">
                <h6 className="bi-section-title">
                    <span className="icon-glyph glyphicon-eye-open"></span>Views
                </h6>
                {items.map((section, idx) => (
                    <div
                        key={idx}
                        className="bi-report-area"
                    >
                        <h6 className="bi-title">{section.title}</h6>
                        <ul className="bi-reports">
                            {section.reports.map((rpt, idx) => (
                                <li key={idx}>
                                    {rpt.claim == activeClaim && <span className="icon-glyph icon-arrow-right"></span>}
                                    <a onClick={e => handleClickMenu(e, rpt)}>{rpt.name}</a>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
                <h6>
                    <span className="bi-version">{_biStore.current.biVersion}</span>
                </h6>
            </section>
        );
    };

    // RENDER: MAIN
    const isInit = render == BiRender.Init;
    return (
        <>
            <PageTopComp
                title="Insights Facility Reports"
                icon="sprite-money"
            />
            <div className="bi-feature">
                {!isInit && (
                    <BiContent
                        render={render}
                        openPopup={openPopup}
                    />
                )}
                <LazyScriptLoader
                    id="bi-tableau"
                    src="vendor/tableau/tableau.min.js"
                    type="module"
                />
            </div>
        </>
    );
};

export default ReportBiCtl;
